import type {
  AcceptMode,
  SelectablePurpose,
  SelectionState,
} from "../types";
import { productPurposeKey } from "../core/productMatrix";

/**
 * Composite key for a data element's checkbox state — a data element uuid is
 * only unique within its purpose, since the same element can be attached to
 * several purposes on one notice.
 */
export const dataElementKey = (
  purposeUuid: string,
  elementUuid: string,
  productUuid?: string
) => `${productPurposeKey(productUuid, purposeUuid)}-${elementUuid}`;

/**
 * Resolves the checkbox state that a given accept button should submit.
 *
 * - `all` selects every purpose and every enabled data element.
 * - `selected` is the user's own ticks, returned untouched.
 */
export const buildSelectionForMode = (
  rows: ReadonlyArray<{
    purpose: SelectablePurpose;
    productUuid?: string;
  }>,
  mode: AcceptMode,
  current: SelectionState
): SelectionState => {
  if (mode === "selected") {
    return current;
  }

  const selectedPurposes: Record<string, boolean> = {};
  const selectedDataElements: Record<string, boolean> = {};

  rows.forEach(({ purpose, productUuid }) => {
    selectedPurposes[productPurposeKey(productUuid, purpose.uuid)] = true;

    purpose.data_elements.forEach((element) => {
      selectedDataElements[
        dataElementKey(purpose.uuid, element.uuid, productUuid)
      ] = element.enabled;
    });
  });

  return { selectedPurposes, selectedDataElements };
};

/** Both flags, matching the ledger: a disabled element is one that is not
 * there. */
export const isBlockingRequiredElement = (element: {
  enabled: boolean;
  required: boolean;
}): boolean => element.required && element.enabled;

/** The one mandatory predicate: the asterisk, the product control and the
 * confirm gate all read this. */
export const hasRequiredDataElement = (purpose: SelectablePurpose): boolean =>
  purpose.data_elements.some(isBlockingRequiredElement);

/**
 * The data elements a tick grants: the mandatory ones where the purpose has
 * any, every live one where it has none.
 *
 * One rule for the purpose tick and the product tick alike, so ticking a
 * product never records consent for optional data the visitor did not choose.
 * A disabled element is never granted by either.
 */
export const seededDataElements = (purpose: SelectablePurpose): Set<string> => {
  const mandatory = purpose.data_elements.filter(isBlockingRequiredElement);
  const granted =
    mandatory.length > 0
      ? mandatory
      : purpose.data_elements.filter((element) => element.enabled);
  return new Set(granted.map((element) => element.uuid));
};
